import React, { useEffect, useState } from 'react';
import { ArrowLeft, ThumbsUp, ThumbsDown, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import FilePreview from '../help_center/FilePreview';
import { collectAppendedArticleVideos } from '../../utils/articleVideos';
import { withPosterFrame } from '../../utils/videoAnalysis';

interface HelpCenterArticleViewProps {
  agentId: string;
  slug: string;
  brandColor: string;
  isDark: boolean;
  onBack: () => void;
}

interface ArticleData {
  title: string;
  content: string;
  thumbnail?: string;
  preview_type?: 'markdown' | 'text' | 'table';
  preview_data?: { columns: string[]; rows: string[][] };
  video_urls?: string[];
  media_videos?: Array<{ url: string; label?: string; thumbnail?: string | null }>;
}

const API_BASE = import.meta.env.VITE_API_URL || '';

const HelpCenterArticleView: React.FC<HelpCenterArticleViewProps> = ({
  agentId,
  slug,
  brandColor,
  isDark,
  onBack,
}) => {
  const [article, setArticle] = useState<ArticleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(`${API_BASE}/api/widget/help-center/${agentId}/article/${slug}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (!cancelled) setArticle(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load article');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [agentId, slug]);

  const handleFeedback = (vote: 'up' | 'down') => {
    if (feedback) return; // already voted
    setFeedback(vote);
    fetch(`${API_BASE}/api/widget/help-center/${agentId}/article/${slug}/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ helpful: vote === 'up' }),
    }).catch(() => {
      // Non-critical — silently ignore
    });
  };

  return (
    <div className={`absolute inset-0 flex flex-col ${isDark ? 'bg-black text-white' : 'bg-white text-gray-900'}`}>
      {/* Top bar */}
      <div
        className="flex items-center gap-2 px-3 py-2.5 border-b flex-shrink-0"
        style={{ borderColor: isDark ? '#374151' : '#e5e7eb' }}
      >
        <button
          onClick={onBack}
          className={`p-1.5 rounded-full transition-colors ${
            isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
          }`}
          title="Back to chat"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <span
          className={`text-xs font-medium truncate flex-1 ${
            isDark ? 'text-gray-400' : 'text-gray-500'
          }`}
        >
          Help Center Article
        </span>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: brandColor }} />
          </div>
        )}

        {error && (
          <div className="p-4 text-center">
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Could not load article.
            </p>
            <button
              onClick={onBack}
              className="mt-3 text-xs font-medium"
              style={{ color: brandColor }}
            >
              Back to chat
            </button>
          </div>
        )}

        {article && (
          <div className="p-4">
            {/* Title */}
            <h1
              className="text-lg font-bold leading-snug mb-4"
              style={{ color: isDark ? '#ffffff' : '#111827', borderBottomWidth: '2px', borderBottomColor: brandColor, paddingBottom: '0.5rem' }}
            >
              {article.title}
            </h1>

            {/* Article content */}
            {article.preview_type ? (
              <FilePreview
                preview_type={article.preview_type}
                content={article.content}
                preview_data={article.preview_data}
              />
            ) : (
              <div
                className={`prose prose-sm max-w-none ${isDark ? 'prose-invert' : ''}`}
                style={{ color: isDark ? '#e5e7eb' : '#374151' }}
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    h1: ({ node, ...props }) => <h1 {...props} style={{ color: isDark ? '#ffffff' : '#111827' }} />,
                    h2: ({ node, ...props }) => <h2 {...props} style={{ color: isDark ? '#ffffff' : '#111827' }} />,
                    h3: ({ node, ...props }) => <h3 {...props} style={{ color: isDark ? '#ffffff' : '#1f2937' }} />,
                    h4: ({ node, ...props }) => <h4 {...props} style={{ color: isDark ? '#ffffff' : '#1f2937' }} />,
                    p: ({ node, ...props }) => <p {...props} style={{ color: isDark ? '#e5e7eb' : '#374151' }} />,
                    a: ({ node, ...props }) => (
                      <a
                        {...props}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: brandColor }}
                      />
                    ),
                    img: ({ src, alt }) => (
                      <a href={src} target="_blank" rel="noopener noreferrer">
                        <img
                          src={src}
                          alt={alt || ''}
                          className="max-w-full h-auto rounded-lg my-2 cursor-pointer"
                        />
                      </a>
                    ),
                    // Raw <video> tags in stored article HTML (via rehypeRaw)
                    // need playsInline + a #t=0.1 poster-frame fragment or iOS
                    // Safari paints a blank thumbnail. Fixes already-stored
                    // articles, not just new uploads.
                    video: ({ node, src, children, ...props }) => (
                      <video
                        {...props}
                        controls
                        playsInline
                        preload="metadata"
                        src={typeof src === 'string' ? withPosterFrame(src) : undefined}
                      >
                        {children}
                      </video>
                    ),
                    // Child <source> tags of raw <video> blocks get the same
                    // fragment treatment (fragments are client-only, so safe
                    // on presigned/query-param URLs).
                    source: ({ node, src, ...props }) => (
                      <source
                        {...props}
                        src={typeof src === 'string' ? withPosterFrame(src) : undefined}
                      />
                    ),
                  }}
                >
                  {article.content}
                </ReactMarkdown>
              </div>
            )}

            {/* Appended videos — sidecar entries not already inline in content */}
            {(() => {
              const appended = collectAppendedArticleVideos(article.content, {
                media_videos: article.media_videos,
                video_urls: article.video_urls,
              });
              if (!appended.length) return null;
              return (
                <section className="mt-6">
                  <h2
                    className={`text-[10px] font-semibold uppercase tracking-wider mb-2 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    Videos
                  </h2>
                  <div className="space-y-3">
                    {appended.map((v, i) => (
                      <figure
                        key={`av-${i}`}
                        className={`not-prose w-full rounded-lg overflow-hidden border ${
                          isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
                        }`}
                      >
                        <video
                          controls
                          playsInline
                          preload="metadata"
                          poster={v.thumbnail}
                          aria-label={v.label}
                          className="block w-full max-w-full h-auto bg-black"
                        >
                          <source src={withPosterFrame(v.url.replace(/ /g, '%20'))} />
                        </video>
                        {v.label && (
                          <figcaption
                            className={`px-3 py-2 text-xs ${
                              isDark ? 'text-gray-300' : 'text-gray-600'
                            }`}
                          >
                            {v.label}
                          </figcaption>
                        )}
                      </figure>
                    ))}
                  </div>
                </section>
              );
            })()}

            {/* Feedback */}
            <div
              className={`mt-6 pt-4 border-t ${
                isDark ? 'border-gray-700' : 'border-gray-200'
              }`}
            >
              <p
                className={`text-xs font-medium mb-2 ${
                  isDark ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                Was this helpful?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => handleFeedback('up')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    feedback === 'up'
                      ? 'text-white'
                      : isDark
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-800'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                  }`}
                  style={feedback === 'up' ? { backgroundColor: brandColor, borderColor: brandColor } : undefined}
                >
                  <ThumbsUp className="w-3.5 h-3.5" />
                  Yes
                </button>
                <button
                  onClick={() => handleFeedback('down')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    feedback === 'down'
                      ? 'bg-gray-500 text-white border-gray-500'
                      : isDark
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-800'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <ThumbsDown className="w-3.5 h-3.5" />
                  No
                </button>
              </div>
              {feedback && (
                <p className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Thanks for your feedback!
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpCenterArticleView;
